import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface LanguageSwitcherProps {
  variant?: 'default' | 'compact';
  openAbove?: boolean;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'default', openAbove = false }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];
  
  if (!i18n.isInitialized || !i18n.language) {
    return (
      <div className="flex items-center space-x-2 text-white/80">
        <span className="text-lg">🇪🇸</span>
        {variant === 'compact' ? (
          <span className="text-sm">...</span>
        ) : (
          <span>Loading...</span>
        )}
      </div>
    );
  }

  const handleLanguageChange = async (langCode: string) => {
    if (langCode !== i18n.language) {
      setIsChanging(true);
      try {
        await i18n.changeLanguage(langCode);
        setIsOpen(false);
      } catch (error) {
        console.error('Error changing language:', error);
      } finally {
        setIsChanging(false);
      }
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        portalRef.current &&
        !portalRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => {
          if (!isChanging) {
            setIsOpen(!isOpen);
          }
        }}
        onKeyDown={(e) => {
          if (!isChanging && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        className={`flex items-center space-x-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-secondary/50 rounded-lg ${
          variant === 'compact' 
            ? 'px-3 py-2 justify-center w-full bg-white/5 hover:bg-white/10 border border-white/10' 
            : 'px-3 py-2 hover:bg-white/5'
        } ${
          isChanging 
            ? 'text-white/50 cursor-not-allowed' 
            : 'text-white/90 hover:text-brand-secondary'
        }`}
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        disabled={isChanging}
      >
        <span className="text-lg">{currentLanguage.flag}</span>
        {variant === 'compact' ? (
          <span className="text-sm">{currentLanguage.code.toUpperCase()}</span>
        ) : (
          <span>
            {isChanging ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white/20 border-t-white/80 rounded-full animate-spin"></div>
                <span>Changing...</span>
              </div>
            ) : (
              `${currentLanguage.name}`
            )}
          </span>
        )}
        {variant !== 'compact' && (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {isOpen && createPortal(
        <div 
          ref={portalRef}
          className={`fixed bg-slate-900/95 backdrop-blur-xl border border-brand-secondary/30 rounded-xl shadow-2xl shadow-brand-secondary/20 z-[9999] transform transition-all duration-200 ease-out opacity-100 scale-100 ${
            variant === 'compact' 
              ? 'left-0 right-0 w-full' 
              : 'w-56'
          }`}
          style={{
            top: dropdownRef.current ? (openAbove ? dropdownRef.current.getBoundingClientRect().top - 8 : dropdownRef.current.getBoundingClientRect().bottom + 8) : 0,
            left: variant === 'compact' ? 0 : (dropdownRef.current ? dropdownRef.current.getBoundingClientRect().left : 0),
            transform: openAbove ? 'translateY(-100%)' : 'none',
            pointerEvents: 'auto'
          }}
          role="listbox"
          aria-label="Language options"
        >
          <div className="p-2">
            {languages.map((language) => (
              <button
                key={language.code}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isChanging) {
                    handleLanguageChange(language.code);
                  }
                }}
                onKeyDown={(e) => {
                  if (!isChanging && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleLanguageChange(language.code);
                  }
                }}
                className={`w-full text-left px-4 py-3 text-sm transition-colors duration-200 flex items-center space-x-3 focus:outline-none rounded-lg ${
                  isChanging 
                    ? 'text-white/50 cursor-not-allowed' 
                    : i18n.language.startsWith(language.code)
                      ? 'text-brand-secondary bg-brand-secondary/10 cursor-pointer border border-brand-secondary/30' 
                      : 'text-white/80 hover:text-white hover:bg-white/10 cursor-pointer'
                }`}
                style={{ pointerEvents: 'auto', position: 'relative', zIndex: 10000 }}
                tabIndex={0}
                role="option"
                aria-selected={i18n.language.startsWith(language.code)}
                disabled={isChanging}
              >
                <span className="text-lg">{language.flag}</span>
                <span className="font-medium">{language.name}</span>
                {i18n.language.startsWith(language.code) && (
                  <svg className="w-5 h-5 ml-auto text-brand-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default LanguageSwitcher; 